import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useEditor } from '../../contexts/EditorContext';
import './RightSidebar.css';

const RightSidebar = () => {
  const { state, actions } = useEditor();
  const [selectedFont, setSelectedFont] = useState(state.design.fontFamily);
  const [fontSize, setFontSize] = useState(state.design.fontSize);
  const [lineHeight, setLineHeight] = useState(state.design.lineHeight);
  const [letterSpacing, setLetterSpacing] = useState(state.design.letterSpacing);
  const [textColor, setTextColor] = useState(state.design.textColor);
  const [backgroundColor, setBackgroundColor] = useState(state.design.backgroundColor);
  const [selectedElementStyles, setSelectedElementStyles] = useState(null);
  
  const selectedElementRef = useRef(null);
  const editorRef = useRef(null);

  const fontFamilies = useMemo(() => [
    { name: 'Inter', value: 'Inter, system-ui' },
    { name: 'Roboto', value: 'Roboto, sans-serif' },
    { name: 'Poppins', value: 'Poppins, sans-serif' },
    { name: 'Open Sans', value: 'Open Sans, sans-serif' },
    { name: 'Montserrat', value: 'Montserrat, sans-serif' },
    { name: 'Lato', value: 'Lato, sans-serif' },
    { name: 'Merriweather', value: 'Merriweather, serif' },
    { name: 'Playfair Display', value: 'Playfair Display, serif' },
    { name: 'Georgia', value: 'Georgia, serif' },
    { name: 'Arial', value: 'Arial, sans-serif' },
    { name: 'Times New Roman', value: 'Times New Roman, serif' },
    { name: 'Courier New', value: 'Courier New, monospace' }
  ], []);

  // Sync with global state when no element is selected
  useEffect(() => {
    if (!state.selectedElement) {
      setSelectedFont(state.design.fontFamily);
      setFontSize(state.design.fontSize);
      setLineHeight(state.design.lineHeight);
      setLetterSpacing(state.design.letterSpacing);
      setTextColor(state.design.textColor);
      setBackgroundColor(state.design.backgroundColor);
      setSelectedElementStyles(null);
      selectedElementRef.current = null;
    }
  }, [state.design, state.selectedElement]);

  // Get editor reference
  useEffect(() => {
    editorRef.current = document.querySelector('#editor');
  }, []);

  // Store selected element reference
  useEffect(() => {
    if (state.selectedElement?.element) {
      try {
        selectedElementRef.current = state.selectedElement.element;
        
        const element = state.selectedElement.element;
        const styles = window.getComputedStyle(element);
        
        const inlineFontFamily = element.style.fontFamily;
        const inlineFontSize = element.style.fontSize;
        const inlineColor = element.style.color;
        const inlineBackgroundColor = element.style.backgroundColor;
        
        setSelectedElementStyles({
          fontFamily: inlineFontFamily || styles.fontFamily,
          fontSize: inlineFontSize ? parseInt(inlineFontSize) : parseInt(styles.fontSize) || fontSize,
          color: inlineColor || styles.color,
          backgroundColor: inlineBackgroundColor || 
                         (styles.backgroundColor !== 'rgba(0, 0, 0, 0)' ? styles.backgroundColor : 'transparent'),
          textAlign: element.style.textAlign || styles.textAlign,
          fontWeight: element.style.fontWeight || styles.fontWeight,
          fontStyle: element.style.fontStyle || styles.fontStyle,
          textDecoration: element.style.textDecoration || styles.textDecoration,
          lineHeight: element.style.lineHeight || parseFloat(styles.lineHeight) / (parseInt(styles.fontSize) || fontSize) || lineHeight,
          letterSpacing: element.style.letterSpacing || styles.letterSpacing
        });
        
        if (inlineFontFamily) setSelectedFont(inlineFontFamily);
        else if (styles.fontFamily !== selectedFont) setSelectedFont(styles.fontFamily);
        
        if (inlineFontSize) setFontSize(parseInt(inlineFontSize));
        else if (parseInt(styles.fontSize) !== fontSize) setFontSize(parseInt(styles.fontSize) || fontSize);
        
        if (inlineColor) setTextColor(inlineColor);
        else if (styles.color !== textColor) setTextColor(styles.color);
      } catch (error) {
        console.error('Error getting element styles:', error);
        selectedElementRef.current = null;
        setSelectedElementStyles(null);
      }
    } else {
      selectedElementRef.current = null;
      setSelectedElementStyles(null);
    }
  }, [state.selectedElement, fontSize, lineHeight, selectedFont, textColor]);

  // FIXED: Immediate content save function
  const saveContentImmediately = useCallback(() => {
    const editor = editorRef.current;
    if (editor) {
      try {
        actions.updatePageContent(editor.innerHTML);
      } catch (error) {
        console.error('Error saving content:', error);
      }
    }
  }, [actions]);

  // FIXED: Direct style application with immediate save
  const applyStyleToElementDirect = useCallback((property, value) => {
    const element = selectedElementRef.current;
    if (!element) return;
    
    try {
      switch(property) {
        case 'fontFamily':
          element.style.setProperty('font-family', value, 'important');
          break;
        case 'fontSize':
          element.style.setProperty('font-size', `${value}px`, 'important');
          break;
        case 'color':
          element.style.setProperty('color', value, 'important');
          break;
        case 'backgroundColor':
          element.style.setProperty('background-color', value, 'important');
          break;
        case 'textAlign':
          element.style.textAlign = value;
          break;
        case 'fontWeight':
          element.style.fontWeight = value;
          break;
        case 'fontStyle':
          element.style.fontStyle = value;
          break;
        case 'textDecoration':
          element.style.textDecoration = value;
          break;
        case 'lineHeight':
          element.style.lineHeight = value;
          break;
        case 'letterSpacing':
          element.style.letterSpacing = value;
          break;
        default:
          if (property in element.style) {
            element.style[property] = value;
          }
      }
      
      setSelectedElementStyles(prev => ({
        ...prev,
        [property]: value
      }));
      
      // FIXED: Save immediately after style change
      saveContentImmediately();
    } catch (error) {
      console.error('Error applying style:', error);
    }
  }, [saveContentImmediately]);

  // FIXED: Document-wide style application with immediate save
  const applyStyleToDocument = useCallback((property, value) => {
    const editor = editorRef.current;
    if (!editor) return;
    
    // Update design state
    actions.updateDesign({ [property]: value });
    
    // Get or create style tag for document-level styles
    let styleTag = document.getElementById('dynamic-editor-styles');
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = 'dynamic-editor-styles';
      document.head.appendChild(styleTag);
    }
    
    let cssRules = '';
    
    if (property === 'backgroundColor') {
      cssRules = `
        #editor {
          background-color: ${value};
        }
        #editor > div:not([style*="background-color"]):not([style*="background"]),
        #editor > section:not([style*="background-color"]):not([style*="background"]),
        #editor > aside:not([style*="background-color"]):not([style*="background"]),
        #editor > main:not([style*="background-color"]):not([style*="background"]) {
          background-color: ${value};
        }
      `;
    } else if (property === 'color') {
      cssRules = `
        #editor,
        #editor p:not([style*="color"]),
        #editor h1:not([style*="color"]), 
        #editor h2:not([style*="color"]), 
        #editor h3:not([style*="color"]), 
        #editor h4:not([style*="color"]), 
        #editor h5:not([style*="color"]), 
        #editor h6:not([style*="color"]),
        #editor span:not([style*="color"]), 
        #editor div:not([style*="color"]):not(.element-actions), 
        #editor li:not([style*="color"]), 
        #editor td:not([style*="color"]), 
        #editor th:not([style*="color"]) {
          color: ${value};
        }
      `;
    } else if (property === 'fontFamily') {
      cssRules = `
        #editor,
        #editor p:not([style*="font-family"]),
        #editor h1:not([style*="font-family"]), 
        #editor h2:not([style*="font-family"]), 
        #editor h3:not([style*="font-family"]), 
        #editor h4:not([style*="font-family"]), 
        #editor h5:not([style*="font-family"]), 
        #editor h6:not([style*="font-family"]),
        #editor span:not([style*="font-family"]), 
        #editor div:not([style*="font-family"]):not(.element-actions), 
        #editor li:not([style*="font-family"]), 
        #editor td:not([style*="font-family"]), 
        #editor th:not([style*="font-family"]) {
          font-family: ${value};
        }
      `;
    } else if (property === 'fontSize') {
      cssRules = `
        #editor,
        #editor p:not([style*="font-size"]),
        #editor h1:not([style*="font-size"]), 
        #editor h2:not([style*="font-size"]), 
        #editor h3:not([style*="font-size"]), 
        #editor h4:not([style*="font-size"]), 
        #editor h5:not([style*="font-size"]), 
        #editor h6:not([style*="font-size"]),
        #editor span:not([style*="font-size"]), 
        #editor div:not([style*="font-size"]):not(.element-actions), 
        #editor li:not([style*="font-size"]), 
        #editor td:not([style*="font-size"]), 
        #editor th:not([style*="font-size"]) {
          font-size: ${value}px;
        }
      `;
    } else if (property === 'lineHeight') {
      cssRules = `
        #editor,
        #editor p:not([style*="line-height"]),
        #editor h1:not([style*="line-height"]), 
        #editor h2:not([style*="line-height"]), 
        #editor h3:not([style*="line-height"]), 
        #editor h4:not([style*="line-height"]), 
        #editor h5:not([style*="line-height"]), 
        #editor h6:not([style*="line-height"]),
        #editor span:not([style*="line-height"]), 
        #editor div:not([style*="line-height"]):not(.element-actions), 
        #editor li:not([style*="line-height"]), 
        #editor td:not([style*="line-height"]), 
        #editor th:not([style*="line-height"]) {
          line-height: ${value};
        }
      `;
    } else if (property === 'letterSpacing') {
      cssRules = `
        #editor,
        #editor p:not([style*="letter-spacing"]),
        #editor h1:not([style*="letter-spacing"]), 
        #editor h2:not([style*="letter-spacing"]), 
        #editor h3:not([style*="letter-spacing"]), 
        #editor h4:not([style*="letter-spacing"]), 
        #editor h5:not([style*="letter-spacing"]), 
        #editor h6:not([style*="letter-spacing"]),
        #editor span:not([style*="letter-spacing"]), 
        #editor div:not([style*="letter-spacing"]):not(.element-actions), 
        #editor li:not([style*="letter-spacing"]), 
        #editor td:not([style*="letter-spacing"]), 
        #editor th:not([style*="letter-spacing"]) {
          letter-spacing: ${value};
        }
      `;
    }
    
    if (cssRules) {
      const currentCSS = styleTag.textContent || '';
      const cleanCSS = currentCSS.replace(new RegExp(`[^{]*{[^}]*${property}[^}]*}[^{]*`, 'g'), '');
      styleTag.textContent = cleanCSS + cssRules;
    }
    
    // FIXED: Save immediately after document style change
    saveContentImmediately();
  }, [actions, saveContentImmediately]);

  // FIXED: Immediate color change
  const handleColorChange = (type, color) => {
    if (type === 'text') {
      setTextColor(color);
      if (selectedElementRef.current) {
        applyStyleToElementDirect('color', color);
      } else {
        applyStyleToDocument('color', color);
      }
    } else {
      setBackgroundColor(color);
      if (selectedElementRef.current) {
        applyStyleToElementDirect('backgroundColor', color);
      } else {
        applyStyleToDocument('backgroundColor', color);
      }
    }
  };

  // FIXED: Immediate font change
  const handleFontChange = (font) => {
    setSelectedFont(font);
    if (selectedElementRef.current) {
      applyStyleToElementDirect('fontFamily', font);
    } else {
      applyStyleToDocument('fontFamily', font);
    }
  };

  // FIXED: Immediate font size change
  const handleFontSizeChange = (change) => {
    if (selectedElementRef.current) {
      const current = selectedElementStyles?.fontSize || fontSize;
      const newSize = Math.max(8, Math.min(72, current + change));
      setFontSize(newSize);
      applyStyleToElementDirect('fontSize', newSize);
    } else {
      const newSize = Math.max(8, Math.min(72, fontSize + change));
      setFontSize(newSize);
      applyStyleToDocument('fontSize', newSize);
    }
  };

  const handleFontSizeInputChange = (e) => {
    const value = parseInt(e.target.value) || 14;
    if (value >= 8 && value <= 72) {
      setFontSize(value);
      if (selectedElementRef.current) {
        applyStyleToElementDirect('fontSize', value);
      } else {
        applyStyleToDocument('fontSize', value);
      }
    }
  };

  const handleLineHeightChange = (change) => {
    const changeAmount = change * 0.1;
    if (selectedElementRef.current) {
      const current = selectedElementStyles?.lineHeight || lineHeight;
      const newHeight = Math.max(1, Math.min(3, parseFloat((current + changeAmount).toFixed(1))));
      setLineHeight(newHeight);
      applyStyleToElementDirect('lineHeight', newHeight);
    } else {
      const newHeight = Math.max(1, Math.min(3, parseFloat((lineHeight + changeAmount).toFixed(1))));
      setLineHeight(newHeight);
      applyStyleToDocument('lineHeight', newHeight);
    }
  };

  const handleLineHeightInputChange = (e) => {
    const value = parseFloat(e.target.value) || 1.6;
    if (value >= 1 && value <= 3) {
      setLineHeight(value);
      if (selectedElementRef.current) {
        applyStyleToElementDirect('lineHeight', value);
      } else {
        applyStyleToDocument('lineHeight', value);
      }
    }
  };

  const handleLetterSpacingChange = (change) => {
    const changeAmount = change * 0.1;
    if (selectedElementRef.current) {
      const current = selectedElementStyles?.letterSpacing === 'normal' ? 0 : 
                    parseFloat(selectedElementStyles?.letterSpacing) || 0;
      const newSpacing = Math.max(-2, Math.min(10, current + changeAmount));
      const newValue = newSpacing === 0 ? 'normal' : `${newSpacing}px`;
      setLetterSpacing(newValue);
      applyStyleToElementDirect('letterSpacing', newValue);
    } else {
      const current = letterSpacing === 'normal' ? 0 : parseFloat(letterSpacing) || 0;
      const newSpacing = Math.max(-2, Math.min(10, current + changeAmount));
      const newValue = newSpacing === 0 ? 'normal' : `${newSpacing}px`;
      setLetterSpacing(newValue);
      applyStyleToDocument('letterSpacing', newValue);
    }
  };

  const handleLetterSpacingInputChange = (e) => {
    const value = e.target.value;
    setLetterSpacing(value);
    if (selectedElementRef.current) {
      applyStyleToElementDirect('letterSpacing', value);
    } else {
      applyStyleToDocument('letterSpacing', value);
    }
  };

  const presetColors = useMemo(() => [
    '#1a365d', '#2d3748', '#4a5568', '#718096',
    '#1ca29b', '#2b6cb0', '#38a169', '#d53f8c',
    '#dd6b20', '#e53e3e', '#805ad5', '#d69e2e'
  ], []);

  return (
    <div className="right-sidebar">
      <div className="sidebar-header">
        <h3>Properties</h3>
        {selectedElementRef.current ? (
          <span className="selection-count">Element Selected</span>
        ) : (
          <span className="selection-count">Document Settings</span>
        )}
      </div>

      <div className="properties-panel">
        {!selectedElementRef.current ? (
          <div className="document-properties">
            <h4>Document Settings</h4>
            
            <div className="property-group">
              <label>Font Family</label>
              <select 
                className="font-select"
                value={selectedFont}
                onChange={(e) => handleFontChange(e.target.value)}
                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
              >
                {fontFamilies.map(font => (
                  <option key={font.value} value={font.value}>
                    {font.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="property-group">
              <label>Typography</label>
              <div className="typography-controls">
                <div className="control-row">
                  <span>Font Size</span>
                  <div className="number-control">
                    <button onClick={() => handleFontSizeChange(-1)}>-</button>
                    <input
                      type="number"
                      value={fontSize}
                      onChange={handleFontSizeInputChange}
                      min="8"
                      max="72"
                    />
                    <button onClick={() => handleFontSizeChange(1)}>+</button>
                  </div>
                </div>

                <div className="control-row">
                  <span>Line Height</span>
                  <div className="number-control">
                    <button onClick={() => handleLineHeightChange(-1)}>-</button>
                    <input
                      type="number"
                      value={lineHeight}
                      step="0.1"
                      onChange={handleLineHeightInputChange}
                      min="1"
                      max="3"
                    />
                    <button onClick={() => handleLineHeightChange(1)}>+</button>
                  </div>
                </div>

                <div className="control-row">
                  <span>Letter Spacing</span>
                  <div className="number-control">
                    <button onClick={() => handleLetterSpacingChange(-1)}>-</button>
                    <input
                      type="text"
                      value={letterSpacing}
                      onChange={handleLetterSpacingInputChange}
                    />
                    <button onClick={() => handleLetterSpacingChange(1)}>+</button>
                  </div>
                </div>
              </div>
            </div>

            <div className="property-group">
              <label>Colors</label>
              <div className="color-controls">
                <div className="color-picker-group">
                  <span>Text Color</span>
                  <div className="color-input">
                    <input
                      type="color"
                      value={textColor}
                      onChange={(e) => handleColorChange('text', e.target.value)}
                    />
                    <span className="color-value">{textColor}</span>
                  </div>
                </div>
                <div className="color-picker-group">
                  <span>Background</span>
                  <div className="color-input">
                    <input
                      type="color"
                      value={backgroundColor}
                      onChange={(e) => handleColorChange('background', e.target.value)}
                    />
                    <span className="color-value">{backgroundColor}</span>
                  </div>
                </div>
                <div className="color-presets">
                  {presetColors.map(color => (
                    <button
                      key={color}
                      className="color-preset"
                      style={{ backgroundColor: color }}
                      onClick={() => handleColorChange('text', color)}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="property-group">
              <label>Spacing</label>
              <div className="spacing-controls">
                <div className="spacing-control">
                  <span>Padding</span>
                  <div className="number-control">
                    <button onClick={() => {
                      const current = parseInt(state.design.padding) || 40;
                      const newValue = Math.max(0, Math.min(100, current - 5));
                      actions.updateDesign({ padding: `${newValue}px` });
                      saveContentImmediately();
                    }}>-</button>
                    <span>{state.design.padding}</span>
                    <button onClick={() => {
                      const current = parseInt(state.design.padding) || 40;
                      const newValue = Math.max(0, Math.min(100, current + 5));
                      actions.updateDesign({ padding: `${newValue}px` });
                      saveContentImmediately();
                    }}>+</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="element-properties">
            <h4>Element Properties</h4>
            
            <div className="property-group">
              <label>Typography</label>
              <div className="typography-controls">
                <div className="control-row">
                  <span>Font</span>
                  <select 
                    className="font-select"
                    value={selectedElementStyles?.fontFamily || selectedFont}
                    onChange={(e) => handleFontChange(e.target.value)}
                    style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #e2e8f0' }}
                  >
                    {fontFamilies.map(font => (
                      <option key={font.value} value={font.value}>
                        {font.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="control-row">
                  <span>Size</span>
                  <div className="number-control">
                    <button onClick={() => handleFontSizeChange(-1)}>-</button>
                    <input
                      type="number"
                      value={selectedElementStyles?.fontSize || fontSize}
                      onChange={handleFontSizeInputChange}
                      min="8"
                      max="72"
                    />
                    <button onClick={() => handleFontSizeChange(1)}>+</button>
                  </div>
                </div>

                <div className="control-row">
                  <span>Line Height</span>
                  <div className="number-control">
                    <button onClick={() => handleLineHeightChange(-1)}>-</button>
                    <input
                      type="number"
                      value={selectedElementStyles?.lineHeight || 1.6}
                      step="0.1"
                      onChange={handleLineHeightInputChange}
                      min="1"
                      max="3"
                    />
                    <button onClick={() => handleLineHeightChange(1)}>+</button>
                  </div>
                </div>

                <div className="control-row">
                  <span>Letter Spacing</span>
                  <div className="number-control">
                    <button onClick={() => handleLetterSpacingChange(-1)}>-</button>
                    <input
                      type="text"
                      value={selectedElementStyles?.letterSpacing || 'normal'}
                      onChange={handleLetterSpacingInputChange}
                    />
                    <button onClick={() => handleLetterSpacingChange(1)}>+</button>
                  </div>
                </div>
              </div>
            </div>

            <div className="property-group">
              <label>Colors</label>
              <div className="color-controls">
                <div className="color-picker-group">
                  <span>Text Color</span>
                  <div className="color-input">
                    <input
                      type="color"
                      value={selectedElementStyles?.color || textColor}
                      onChange={(e) => handleColorChange('text', e.target.value)}
                    />
                  </div>
                </div>
                <div className="color-picker-group">
                  <span>Background</span>
                  <div className="color-input">
                    <input
                      type="color"
                      value={selectedElementStyles?.backgroundColor !== 'rgba(0, 0, 0, 0)' && 
                             selectedElementStyles?.backgroundColor !== 'transparent' ? 
                             selectedElementStyles?.backgroundColor : backgroundColor}
                      onChange={(e) => handleColorChange('background', e.target.value)}
                    />
                  </div>
                </div>
                <div className="color-presets">
                  {presetColors.map(color => (
                    <button
                      key={color}
                      className="color-preset"
                      style={{ backgroundColor: color }}
                      onClick={() => handleColorChange('text', color)}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="property-group">
              <label>Alignment</label>
              <div className="alignment-controls">
                <button 
                  className={`align-btn ${selectedElementStyles?.textAlign === 'left' ? 'active' : ''}`}
                  onClick={() => {
                    applyStyleToElementDirect('textAlign', 'left');
                  }}
                  title="Align Left"
                >
                  <i className="fas fa-align-left"></i>
                </button>
                <button 
                  className={`align-btn ${selectedElementStyles?.textAlign === 'center' ? 'active' : ''}`}
                  onClick={() => {
                    applyStyleToElementDirect('textAlign', 'center');
                  }}
                  title="Align Center"
                >
                  <i className="fas fa-align-center"></i>
                </button>
                <button 
                  className={`align-btn ${selectedElementStyles?.textAlign === 'right' ? 'active' : ''}`}
                  onClick={() => {
                    applyStyleToElementDirect('textAlign', 'right');
                  }}
                  title="Align Right"
                >
                  <i className="fas fa-align-right"></i>
                </button>
                <button 
                  className={`align-btn ${selectedElementStyles?.textAlign === 'justify' ? 'active' : ''}`}
                  onClick={() => {
                    applyStyleToElementDirect('textAlign', 'justify');
                  }}
                  title="Justify"
                >
                  <i className="fas fa-align-justify"></i>
                </button>
              </div>
            </div>

            <div className="property-group">
              <label>Style</label>
              <div className="style-controls">
                <button 
                  className={`style-btn ${selectedElementStyles?.fontWeight === 'bold' || selectedElementStyles?.fontWeight === '700' ? 'active' : ''}`}
                  onClick={() => {
                    const newWeight = selectedElementStyles?.fontWeight === 'bold' || selectedElementStyles?.fontWeight === '700' ? 'normal' : 'bold';
                    applyStyleToElementDirect('fontWeight', newWeight);
                  }}
                  title="Bold"
                >
                  <i className="fas fa-bold"></i>
                </button>
                <button 
                  className={`style-btn ${selectedElementStyles?.fontStyle === 'italic' ? 'active' : ''}`}
                  onClick={() => {
                    const newStyle = selectedElementStyles?.fontStyle === 'italic' ? 'normal' : 'italic';
                    applyStyleToElementDirect('fontStyle', newStyle);
                  }}
                  title="Italic"
                >
                  <i className="fas fa-italic"></i>
                </button>
                <button 
                  className={`style-btn ${selectedElementStyles?.textDecoration?.includes('underline') ? 'active' : ''}`}
                  onClick={() => {
                    const newDecoration = selectedElementStyles?.textDecoration?.includes('underline') ? 'none' : 'underline';
                    applyStyleToElementDirect('textDecoration', newDecoration);
                  }}
                  title="Underline"
                >
                  <i className="fas fa-underline"></i>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RightSidebar;